import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as Alert from '@radix-ui/react-alert-dialog';
import {
    multiFactor,
    TotpMultiFactorGenerator,
    PhoneAuthProvider,
    PhoneMultiFactorGenerator,
    EmailAuthProvider,
    reauthenticateWithCredential,
    RecaptchaVerifier,
    getMultiFactorResolver,
    signOut,
} from 'firebase/auth';
import { doc, updateDoc, arrayUnion, deleteField } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import OtpInput from './OtpInput';
import TotpEnrollmentPanel from './TotpEnrollmentPanel';
import { EyeIcon, EyeOffIcon } from '@heroicons/react/solid';
import { CheckCircleIcon } from '@heroicons/react/outline';

// ---------------------------------------------------------------------------
// SwitchToTotpDialog
// ---------------------------------------------------------------------------
// A multi-step dialog that guides an SMS-MFA user through switching to
// authenticator-app (TOTP) MFA without signing them out.
//
// Flow:
//   Step 1 (password)  – Re-authenticate with email + password.
//   Step 2 (sms)       – Complete the SMS MFA challenge from re-auth.
//   Step 3 (totp)      – Generate TOTP secret, scan QR, verify code.
//                         On success: enroll TOTP → unenroll SMS → update Firestore.
//   Step 4 (success)   – Confirmation screen.
//
// Security guarantees:
//   • User proves identity (password + SMS code) before any changes.
//   • TOTP is enrolled BEFORE SMS is removed — no window with zero factors.
//   • All changes are logged to Firestore mfaAuditTrail.
//   • Password is held in component state only while the dialog is open;
//     cleared on close via useEffect cleanup.
//
// Reuse across user types:
//   The dialog originally only served providers, so it wrote MFA metadata to
//   `clinicians/{uid}`. Service users (patients / autism informants) store their
//   profile in different collections, so callers can pass `profileDocRef` — a
//   Firestore DocumentReference to the correct profile document (built via
//   getServiceUserProfileDocRef). When omitted, it defaults to `clinicians/{uid}`
//   so existing provider usage is unchanged.
// ---------------------------------------------------------------------------
export default function SwitchToTotpDialog({
    open,
    onOpenChange,
    onSuccess,
    currentMfaFactorUid,
    profileDocRef = null,
}) {
    // -----------------------------------------------------------------------
    // Step management
    // -----------------------------------------------------------------------
    const [step, setStep] = useState('password'); // 'password' | 'sms' | 'totp' | 'success'

    // -----------------------------------------------------------------------
    // Shared state
    // -----------------------------------------------------------------------
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    // -----------------------------------------------------------------------
    // Step 1: Password
    // -----------------------------------------------------------------------
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    // -----------------------------------------------------------------------
    // Step 2: SMS challenge
    // -----------------------------------------------------------------------
    const [resolver, setResolver] = useState(null);
    const [verificationId, setVerificationId] = useState('');
    const [smsCode, setSmsCode] = useState('');
    const [maskedPhone, setMaskedPhone] = useState('');
    const [smsSentAt, setSmsSentAt] = useState(0);
    const [resendSecondsLeft, setResendSecondsLeft] = useState(0);
    const recaptchaVerifierRef = useRef(null);

    // -----------------------------------------------------------------------
    // Step 3: TOTP enrollment
    // -----------------------------------------------------------------------
    const [totpSecret, setTotpSecret] = useState(null);
    const [totpUri, setTotpUri] = useState('');
    const [totpSecretKey, setTotpSecretKey] = useState('');
    const [totpCode, setTotpCode] = useState('');
    const [isTotpReady, setIsTotpReady] = useState(false);
    const [isTotpGenerating, setIsTotpGenerating] = useState(false);
    const [copiedSecret, setCopiedSecret] = useState(false);

    // -----------------------------------------------------------------------
    // Reset all state when the dialog opens/closes
    // -----------------------------------------------------------------------
    useEffect(() => {
        if (!open) {
            // Clear sensitive state when dialog is dismissed
            setStep('password');
            setError('');
            setIsLoading(false);
            setPassword('');
            setShowPassword(false);
            setResolver(null);
            setVerificationId('');
            setSmsCode('');
            setMaskedPhone('');
            setSmsSentAt(0);
            setResendSecondsLeft(0);
            setTotpSecret(null);
            setTotpUri('');
            setTotpSecretKey('');
            setTotpCode('');
            setIsTotpReady(false);
            setIsTotpGenerating(false);
            setCopiedSecret(false);
            // Clean up reCAPTCHA verifier
            if (recaptchaVerifierRef.current) {
                try { recaptchaVerifierRef.current.clear(); } catch (_) {}
                recaptchaVerifierRef.current = null;
            }
        }
    }, [open]);

    // -----------------------------------------------------------------------
    // Hide the reCAPTCHA badge while the dialog is open.
    //
    // Google's invisible reCAPTCHA injects a fixed-position `.grecaptcha-badge`
    // that floats over our dialog content. The reCAPTCHA SDK continuously
    // manages the badge's inline styles, so setting visibility/zIndex via JS
    // gets overridden. The only reliable approach is a `<style>` tag with
    // `!important` that CSS-specificity-overrides anything the SDK sets.
    //
    // The style tag is injected when the dialog opens and removed on close.
    // Google's reCAPTCHA ToS allows hiding the badge as long as you include
    // the required attribution text elsewhere (the sign-in pages already do).
    // -----------------------------------------------------------------------
    useEffect(() => {
        if (!open) return;
        const style = document.createElement('style');
        style.setAttribute('data-switch-mfa-recaptcha-hide', '');
        style.textContent = '.grecaptcha-badge { visibility: hidden !important; }';
        document.head.appendChild(style);
        return () => { style.remove(); };
    }, [open]);

    // -----------------------------------------------------------------------
    // SMS resend countdown timer
    // -----------------------------------------------------------------------
    useEffect(() => {
        if (step !== 'sms' || !smsSentAt) return;
        const update = () => {
            const elapsed = Math.floor((Date.now() - smsSentAt) / 1000);
            setResendSecondsLeft(Math.max(0, 30 - elapsed));
        };
        update();
        const id = setInterval(update, 1000);
        return () => clearInterval(id);
    }, [step, smsSentAt]);

    // -----------------------------------------------------------------------
    // Step 1 handler: Re-authenticate with password
    // -----------------------------------------------------------------------
    // Calling reauthenticateWithCredential on an MFA-enrolled user will
    // throw auth/multi-factor-auth-required — this is expected. We capture
    // the resolver and advance to step 2 (SMS challenge).
    const handlePasswordSubmit = async () => {
        if (isLoading || !password) return;
        setIsLoading(true);
        setError('');

        const user = auth.currentUser;
        if (!user) {
            setError('You are not signed in. Please refresh and try again.');
            setIsLoading(false);
            return;
        }

        try {
            const credential = EmailAuthProvider.credential(user.email, password);
            await reauthenticateWithCredential(user, credential);
            // If re-auth succeeded without MFA challenge (shouldn't happen for
            // MFA-enrolled users, but handle defensively) - skip straight to TOTP.
            setStep('totp');
        } catch (err) {
            if (err?.code === 'auth/multi-factor-auth-required') {
                // Expected — build the resolver and send the SMS code
                try {
                    const mfaResolver = getMultiFactorResolver(auth, err);
                    setResolver(mfaResolver);
                    await sendSmsCode(mfaResolver);
                    setStep('sms');
                } catch (smsErr) {
                    setError('Failed to send SMS code. Please try again.');
                    console.error('[SwitchMFA] SMS send error:', smsErr);
                }
            } else if (
                err?.code === 'auth/wrong-password' ||
                err?.code === 'auth/invalid-credential'
            ) {
                setError('Password is incorrect. Please try again.');
            } else if (err?.code === 'auth/too-many-requests') {
                setError('Too many failed attempts. Please try again later.');
            } else {
                setError('An error occurred. Please try again.');
                console.error('[SwitchMFA] Re-auth error:', err);
            }
        } finally {
            setIsLoading(false);
        }
    };

    // -----------------------------------------------------------------------
    // SMS code sending (used for initial send and resend)
    // -----------------------------------------------------------------------
    const sendSmsCode = async (mfaResolver) => {
        const res = mfaResolver || resolver;
        if (!res) return;

        // Find the phone hint from the resolver.
        // Note: PhoneMultiFactorGenerator.FACTOR_ID === 'phone'. We use the
        // literal string for clarity, matching the pattern in mfaUtils.js.
        const phoneHint = res.hints.find(
            (h) => h.factorId === 'phone'
        );
        if (!phoneHint) {
            setError('No SMS factor found. Cannot proceed.');
            return;
        }

        // Extract masked phone number for display
        const phone = phoneHint.phoneNumber || '';
        const last4 = phone.replace(/\D/g, '').slice(-4);
        setMaskedPhone(last4 ? `••••${last4}` : '');

        // Create reCAPTCHA verifier (invisible)
        if (recaptchaVerifierRef.current) {
            try { recaptchaVerifierRef.current.clear(); } catch (_) {}
            recaptchaVerifierRef.current = null;
        }
        recaptchaVerifierRef.current = new RecaptchaVerifier(
            auth,
            'switch-mfa-recaptcha-container',
            { size: 'invisible', callback: () => {} }
        );

        const provider = new PhoneAuthProvider(auth);
        const newVerificationId = await provider.verifyPhoneNumber(
            { multiFactorHint: phoneHint, session: res.session },
            recaptchaVerifierRef.current
        );
        setVerificationId(newVerificationId);
        setSmsSentAt(Date.now());
    };

    // -----------------------------------------------------------------------
    // Step 2 handler: Verify SMS code
    // -----------------------------------------------------------------------
    const handleSmsVerify = async () => {
        if (isLoading || smsCode.length !== 6 || !resolver) return;
        setIsLoading(true);
        setError('');

        try {
            const cred = PhoneAuthProvider.credential(verificationId, smsCode);
            const assertion = PhoneMultiFactorGenerator.assertion(cred);
            await resolver.resolveSignIn(assertion);
            // Re-auth with MFA complete — session is fresh. Advance to TOTP.
            setStep('totp');
        } catch (err) {
            if (
                err?.code === 'auth/invalid-verification-code' ||
                err?.code === 'auth/code-expired'
            ) {
                setError('Invalid or expired code. Please try again.');
            } else {
                setError('Verification failed. Please try again.');
                console.error('[SwitchMFA] SMS verify error:', err);
            }
        } finally {
            setIsLoading(false);
        }
    };

    // -----------------------------------------------------------------------
    // SMS resend handler
    // -----------------------------------------------------------------------
    const handleSmsResend = async () => {
        if (resendSecondsLeft > 0 || isLoading) return;
        setIsLoading(true);
        setError('');
        try {
            await sendSmsCode(resolver);
        } catch (err) {
            setError('Failed to resend code. Please try again.');
            console.error('[SwitchMFA] SMS resend error:', err);
        } finally {
            setIsLoading(false);
        }
    };

    // -----------------------------------------------------------------------
    // Step 3: Auto-generate TOTP secret when entering the TOTP step
    // -----------------------------------------------------------------------
    const generateTotpSecret = useCallback(async () => {
        if (isTotpGenerating || isTotpReady) return;
        setIsTotpGenerating(true);
        setError('');

        try {
            const user = auth.currentUser;
            if (!user) {
                setError('Session lost. Please close and try again.');
                return;
            }

            const session = await multiFactor(user).getSession();
            const secret = await TotpMultiFactorGenerator.generateSecret(session);
            const uri = secret.generateQrCodeUrl(user.email, 'ThinkDivergent');

            setTotpSecret(secret);
            setTotpUri(uri);
            setTotpSecretKey(secret.secretKey);
            setIsTotpReady(true);
        } catch (err) {
            let msg = 'Failed to generate authenticator setup. ';
            if (err?.code === 'auth/requires-recent-login') {
                msg += 'Your session has expired. Please close this dialog and try again.';
            } else if (err?.code === 'auth/network-request-failed') {
                msg += 'Network error. Check your connection and try again.';
            } else {
                msg += 'Please try again.';
            }
            setError(msg);
            console.error('[SwitchMFA] TOTP secret generation error:', err);
        } finally {
            setIsTotpGenerating(false);
        }
    }, [isTotpGenerating, isTotpReady]);

    // Trigger TOTP secret generation when we arrive at step 3
    useEffect(() => {
        if (step === 'totp' && !isTotpReady && !isTotpGenerating) {
            generateTotpSecret();
        }
    }, [step, isTotpReady, isTotpGenerating, generateTotpSecret]);

    // -----------------------------------------------------------------------
    // Copy secret key to clipboard
    // -----------------------------------------------------------------------
    const handleCopySecret = useCallback(async () => {
        if (!totpSecretKey) return;
        try {
            await navigator.clipboard.writeText(totpSecretKey);
            setCopiedSecret(true);
            setTimeout(() => setCopiedSecret(false), 2000);
        } catch (_) {
            console.warn('[SwitchMFA] Clipboard API not available');
        }
    }, [totpSecretKey]);

    // -----------------------------------------------------------------------
    // Step 3 handler: Verify TOTP code, enroll TOTP, unenroll SMS
    // -----------------------------------------------------------------------
    // This is the critical "atomic switch" operation:
    //   1. Enroll TOTP (user now has [SMS, TOTP])
    //   2. Unenroll SMS (user now has [TOTP] only)
    //   3. Update Firestore metadata
    //   4. Refresh token
    //
    // If step 2 fails, the user safely has both factors. We show a specific
    // message and do NOT mark the switch as complete.
    const handleTotpVerify = async () => {
        if (isLoading || totpCode.length !== 6 || !totpSecret) return;
        setIsLoading(true);
        setError('');

        const user = auth.currentUser;
        if (!user) {
            setError('Session lost. Please close and try again.');
            setIsLoading(false);
            return;
        }

        // Resolve the profile document to update. Defaults to clinicians/{uid}
        // for providers; service-user callers pass their own profileDocRef.
        const profileRef = profileDocRef || doc(db, 'clinicians', user.uid);

        try {
            // Step 3a: Enroll TOTP factor
            const totpAssertion = TotpMultiFactorGenerator.assertionForEnrollment(
                totpSecret,
                totpCode
            );
            await multiFactor(user).enroll(totpAssertion, 'Authenticator App');
            console.log('[SwitchMFA] TOTP enrolled successfully');
        } catch (enrollErr) {
            if (enrollErr?.code === 'auth/invalid-verification-code' ||
                enrollErr?.code === 'auth/code-expired') {
                setError('The code is incorrect or expired. Please enter the latest code from your authenticator app.');
                setIsLoading(false);
                return;
            } else if (enrollErr?.code === 'auth/second-factor-already-in-use') {
                // TOTP already enrolled somehow — proceed to unenroll SMS
                console.warn('[SwitchMFA] TOTP already enrolled, proceeding to unenroll SMS');
            } else {
                setError('Failed to set up authenticator app. Please try again.');
                console.error('[SwitchMFA] TOTP enroll error:', enrollErr);
                setIsLoading(false);
                return;
            }
        }

        // Step 3b: Unenroll SMS factor
        try {
            if (currentMfaFactorUid) {
                await multiFactor(user).unenroll(currentMfaFactorUid);
                console.log('[SwitchMFA] SMS factor unenrolled successfully');
            }
        } catch (unenrollErr) {
            // TOTP IS enrolled but SMS removal failed. User has both factors —
            // this is safe but not the intended end-state.
            console.error('[SwitchMFA] SMS unenroll error:', unenrollErr);

            if (unenrollErr?.code === 'auth/user-token-expired') {
                // Firebase expired the token. Update Firestore before signing out.
                try {
                    await updateDoc(profileRef, {
                        mfaMethod: 'totp',
                        isMFASetUp: true,
                        // Switching away from SMS — clear the now-stale phone number.
                        mfaPhoneNumber: deleteField(),
                        mfaAuditTrail: arrayUnion({
                            action: 'mfa_switch_token_expired',
                            from: 'phone',
                            to: 'totp',
                            source: 'settings_page',
                            note: 'SMS unenroll caused token expiry; Firestore updated before sign-out',
                            at: new Date(),
                        }),
                    });
                } catch (_) {}

                setError(
                    'Your session was refreshed during the switch. ' +
                    'Please sign in again — your authenticator app is now your MFA method.'
                );
                // Sign out gracefully after a brief delay so the user can read the message
                setTimeout(async () => {
                    try { await signOut(auth); } catch (_) {}
                }, 3000);
                setIsLoading(false);
                return;
            }

            // Non-token-expiry failure: log to Firestore and show a specific message
            try {
                await updateDoc(profileRef, {
                    mfaAuditTrail: arrayUnion({
                        action: 'mfa_switch_partial',
                        from: 'phone',
                        to: 'totp',
                        source: 'settings_page',
                        note: 'TOTP enrolled but SMS unenroll failed; user has both factors',
                        error: unenrollErr?.code || 'unknown',
                        at: new Date(),
                    }),
                });
            } catch (_) {}

            setError(
                'Your authenticator app is set up, but we could not remove SMS verification. ' +
                'Please contact support to complete the switch.'
            );
            setIsLoading(false);
            return;
        }

        // Step 3c: Update Firestore metadata
        try {
            await updateDoc(profileRef, {
                mfaMethod: 'totp',
                isMFASetUp: true,
                // Switching away from SMS — clear the now-stale phone number.
                mfaPhoneNumber: deleteField(),
                mfaAuditTrail: arrayUnion({
                    action: 'mfa_switched',
                    from: 'phone',
                    to: 'totp',
                    source: 'settings_page',
                    at: new Date(),
                }),
            });
            console.log('[SwitchMFA] Firestore updated');
        } catch (fsErr) {
            // Non-blocking — the Auth-level switch already succeeded
            console.error('[SwitchMFA] Firestore update error:', fsErr);
        }

        // Step 3d: Refresh the ID token so downstream checks see the new factor state
        try {
            await user.getIdToken(true);
        } catch (_) {}

        // All done — advance to success
        setStep('success');
        setIsLoading(false);
    };

    // -----------------------------------------------------------------------
    // Prevent closing the dialog while a critical operation is in flight
    // -----------------------------------------------------------------------
    const handleOpenChange = (newOpen) => {
        if (!newOpen && isLoading) return; // Block close while loading
        onOpenChange(newOpen);
    };

    // -----------------------------------------------------------------------
    // Render helpers for each step
    // -----------------------------------------------------------------------

    const renderPasswordStep = () => (
        <>
            <Alert.Title className="text-lg font-bold mb-1">
                Switch to Authenticator App
            </Alert.Title>
            <Alert.Description className="mb-4 text-sm text-gray-600">
                To switch your MFA method, please confirm your identity by entering your password.
            </Alert.Description>

            <div className="mb-4">
                <label className="block text-xs font-bold text-gray-700 mb-1">
                    Password
                </label>
                <div className="relative">
                    <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => { setPassword(e.target.value); setError(''); }}
                        placeholder="Enter your current password"
                        className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary pr-10 text-sm"
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && password) handlePasswordSubmit();
                        }}
                        autoFocus
                        disabled={isLoading}
                    />
                    <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        tabIndex={-1}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                        <span className="relative h-4 w-4">
                            <EyeIcon
                                className={`absolute inset-0 h-4 w-4 text-gray-400 transition-all duration-150 ${
                                    showPassword ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
                                }`}
                            />
                            <EyeOffIcon
                                className={`absolute inset-0 h-4 w-4 text-gray-400 transition-all duration-150 ${
                                    showPassword ? 'opacity-0 scale-75' : 'opacity-100 scale-100'
                                }`}
                            />
                        </span>
                    </button>
                </div>
            </div>

            {renderError()}

            <div className="flex justify-end gap-3 mt-4">
                <Alert.Cancel asChild>
                    <button
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                        disabled={isLoading}
                    >
                        Cancel
                    </button>
                </Alert.Cancel>
                <button
                    onClick={handlePasswordSubmit}
                    disabled={isLoading || !password}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary/90 disabled:bg-gray-300 disabled:text-gray-500"
                >
                    {isLoading ? (
                        <span className="flex items-center gap-2">
                            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Verifying...
                        </span>
                    ) : 'Continue'}
                </button>
            </div>
        </>
    );

    const renderSmsStep = () => (
        <>
            <Alert.Title className="text-lg font-bold mb-1">
                Verify Your Identity
            </Alert.Title>
            <Alert.Description className="mb-4 text-sm text-gray-600">
                Enter the 6-digit code sent to your phone
                {maskedPhone ? ` ending in ${maskedPhone}` : ''}.
            </Alert.Description>

            <div className="mb-4">
                <OtpInput
                    value={smsCode}
                    onChange={setSmsCode}
                    disabled={isLoading}
                    autoFocus
                />
            </div>

            {renderError()}

            <div className="flex justify-between items-center mt-4">
                <button
                    type="button"
                    onClick={handleSmsResend}
                    disabled={resendSecondsLeft > 0 || isLoading}
                    className="text-sm text-primary hover:text-primary/80 disabled:text-gray-400"
                >
                    {resendSecondsLeft > 0
                        ? `Resend Code (${resendSecondsLeft}s)`
                        : 'Resend Code'}
                </button>
                <div className="flex gap-3">
                    {!isLoading && (
                        <Alert.Cancel asChild>
                            <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                                Cancel
                            </button>
                        </Alert.Cancel>
                    )}
                    <button
                        onClick={handleSmsVerify}
                        disabled={isLoading || smsCode.length !== 6}
                        className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary/90 disabled:bg-gray-300 disabled:text-gray-500"
                    >
                        {isLoading ? (
                            <span className="flex items-center gap-2">
                                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                Verifying...
                            </span>
                        ) : 'Verify'}
                    </button>
                </div>
            </div>
        </>
    );

    const renderTotpStep = () => (
        <>
            <Alert.Title className="text-lg font-bold mb-1">
                Set Up Authenticator App
            </Alert.Title>
            <Alert.Description className="mb-4 text-sm text-gray-600">
                Scan the QR code below with your authenticator app (e.g. Google Authenticator,
                Microsoft Authenticator), then enter the 6-digit code it generates.
            </Alert.Description>

            {/* Shared authenticator-app enrollment UI (QR + manual key + OTP).
                The intro copy lives in the dialog description above, so we do not
                pass introText or showAppGuidance here. While an error is showing we
                hide the panel and let renderError() below explain what went wrong. */}
            {!error && (
                <TotpEnrollmentPanel
                    isReady={isTotpReady}
                    totpUri={totpUri}
                    totpSecretKey={totpSecretKey}
                    code={totpCode}
                    onCodeChange={setTotpCode}
                    otpDisabled={isLoading}
                    otpAutoFocus
                    copied={copiedSecret}
                    onCopySecret={handleCopySecret}
                    qrSize={160}
                />
            )}

            {renderError()}

            <div className="flex justify-end gap-3 mt-4">
                {!isLoading && (
                    <Alert.Cancel asChild>
                        <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                            Cancel
                        </button>
                    </Alert.Cancel>
                )}
                <button
                    onClick={handleTotpVerify}
                    disabled={isLoading || totpCode.length !== 6 || !totpSecret}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary/90 disabled:bg-gray-300 disabled:text-gray-500"
                >
                    {isLoading ? (
                        <span className="flex items-center gap-2">
                            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Switching...
                        </span>
                    ) : 'Verify & Switch'}
                </button>
            </div>
        </>
    );

    const renderSuccessStep = () => (
        <div className="text-center py-4">
            <div className="flex justify-center mb-4">
                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircleIcon className="w-8 h-8 text-green-600" />
                </div>
            </div>
            <Alert.Title className="text-lg font-bold mb-2">
                MFA Method Switched
            </Alert.Title>
            <Alert.Description className="text-sm text-gray-600 mb-6">
                Your account is now protected with your authenticator app.
                You will use it to verify your identity when signing in.
            </Alert.Description>
            <button
                onClick={() => {
                    onSuccess?.();
                    onOpenChange(false);
                }}
                className="px-6 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary/90"
            >
                Done
            </button>
        </div>
    );

    // -----------------------------------------------------------------------
    // Shared error banner
    // -----------------------------------------------------------------------
    const renderError = () => {
        if (!error) return null;
        return (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-md">
                {error}
            </div>
        );
    };

    // -----------------------------------------------------------------------
    // Step indicator (visual progress dots)
    // -----------------------------------------------------------------------
    const steps = ['password', 'sms', 'totp', 'success'];
    const currentStepIndex = steps.indexOf(step);

    const renderStepIndicator = () => (
        <div className="flex justify-center gap-2 mb-4">
            {steps.map((s, i) => (
                <div
                    key={s}
                    className={`w-2 h-2 rounded-full transition-colors duration-200 ${
                        i <= currentStepIndex ? 'bg-primary' : 'bg-gray-200'
                    }`}
                />
            ))}
        </div>
    );

    // -----------------------------------------------------------------------
    // Main render
    // -----------------------------------------------------------------------
    return (
        <Alert.Root open={open} onOpenChange={handleOpenChange}>
            <Alert.Portal>
                <Alert.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/50 backdrop-blur-sm z-[200]" />
                <Alert.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl p-6 w-[90vw] max-w-[480px] shadow-2xl ring-1 ring-black/5 z-[200] max-h-[90vh] overflow-y-auto">
                    {renderStepIndicator()}

                    {step === 'password' && renderPasswordStep()}
                    {step === 'sms' && renderSmsStep()}
                    {step === 'totp' && renderTotpStep()}
                    {step === 'success' && renderSuccessStep()}

                    {/* Invisible reCAPTCHA container for SMS verification */}
                    <div id="switch-mfa-recaptcha-container" />
                </Alert.Content>
            </Alert.Portal>
        </Alert.Root>
    );
}
